import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { LogOut, Monitor, Moon, Sun } from 'lucide-react'
import { meQueryKey, type Me } from '@/auth/me'
import { WaitingToSend } from '@/components/app/waiting-to-send'
import { ViewAsSwitcher, canViewAs } from '@/components/app/view-as-switcher'
import { Button } from '@/components/ui/button'
import { Card, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/cn'
import { env } from '@/lib/env'
import { supabase } from '@/lib/supabase'
import { setTheme, useTheme, type ThemePref } from '@/lib/theme'
import { enqueue, flushOutbox } from '@/offline/outbox'
import { requestBackgroundSync } from '@/offline/sync'
import { PushOnThisDevice } from '@/features/notifications/push-toggle'

export const Route = createFileRoute('/_app/account')({ component: Account })

const THEMES: { value: ThemePref; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Match device', icon: Monitor },
]

const PREFS = [
  { key: 'push', label: 'Push notifications' },
  { key: 'email', label: 'Email notifications' },
] as const

function Account() {
  const { me } = Route.useRouteContext()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const current = (queryClient.getQueryData<Me>(meQueryKey) ?? me).notification_prefs

  // Saved through the outbox, so it works with no signal and sends itself later.
  const togglePref = async (key: string) => {
    const next = { ...current, [key]: current[key] === false }
    queryClient.setQueryData<Me>(meQueryKey, (old) =>
      old ? { ...old, notification_prefs: next } : old,
    )
    await enqueue({
      type: 'profile.update_prefs',
      label: 'Notification settings',
      userId: me.real_user_id,
      payload: { userId: me.real_user_id, notification_prefs: next },
    })
    void requestBackgroundSync()
    void flushOutbox({ userId: me.real_user_id })
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <h1 className="text-3xl uppercase">Account</h1>

      <Card>
        <CardTitle>{me.full_name}</CardTitle>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
          <dt className="text-fg-muted">Role</dt>
          <dd className="font-semibold">{me.role_label}</dd>
          <dt className="text-fg-muted">Region</dt>
          <dd className="font-semibold">{me.region?.name ?? 'All regions'}</dd>
          <dt className="text-fg-muted">Email</dt>
          <dd className="font-semibold break-all">{me.email}</dd>
          {me.phone && (
            <>
              <dt className="text-fg-muted">Phone</dt>
              <dd className="font-semibold">{me.phone}</dd>
            </>
          )}
        </dl>
      </Card>

      {canViewAs(me) && (
        <Card className="flex flex-col gap-3">
          <CardTitle>View the app as</CardTitle>
          <p className="text-fg-muted">
            See exactly what a rep, a designer or a manager sees. Read-only, and logged in the audit
            log.
          </p>
          <ViewAsSwitcher me={me} className="dark:[&_select]:bg-surface-2" />
        </Card>
      )}

      <WaitingToSend userId={me.real_user_id} />

      <Card>
        <CardTitle>Appearance</CardTitle>
        <div role="radiogroup" aria-label="Theme" className="mt-4 grid grid-cols-3 gap-2">
          {THEMES.map((t) => (
            <button
              key={t.value}
              role="radio"
              aria-checked={theme === t.value}
              onClick={() => setTheme(t.value)}
              className={cn(
                'flex min-h-tap-lg flex-col items-center justify-center gap-1 rounded-control border-2 font-semibold',
                theme === t.value ? 'border-fg bg-surface-2' : 'border-border',
              )}
            >
              <t.icon className="size-5" aria-hidden />
              {t.label}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        <CardTitle>Notifications</CardTitle>
        <ul className="mt-4 flex flex-col gap-2">
          {PREFS.map((p) => {
            const on = current[p.key] !== false
            return (
              <li key={p.key}>
                <button
                  role="switch"
                  aria-checked={on}
                  disabled={me.impersonating}
                  onClick={() => void togglePref(p.key)}
                  className="flex min-h-tap w-full items-center justify-between gap-4 rounded-control px-1 text-left font-semibold disabled:opacity-50"
                >
                  {p.label}
                  <span
                    className={cn(
                      'relative h-8 w-14 rounded-full transition-colors',
                      on ? 'bg-brand' : 'bg-border-strong',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-1 size-6 rounded-full bg-white shadow transition-transform',
                        on ? 'translate-x-7' : 'translate-x-1',
                      )}
                    />
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
        <PushOnThisDevice disabled={me.impersonating} />
      </Card>

      <Button variant="outline" size="lg" onClick={() => void supabase.auth.signOut()}>
        <LogOut aria-hidden /> Sign out
      </Button>
      <p className="text-center text-sm text-fg-muted">MezSites {env.version}</p>
    </div>
  )
}
